'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

type ParamsUpdater = (params: URLSearchParams) => void;
const SYNC_EVENT_NAME = 'sync-search-params-updated';

export function useSyncSearchParams() {
  const nextSearchParams = useSearchParams();
  const [snapshot, setSnapshot] = useState(() => nextSearchParams.toString());

  useEffect(() => {
    setSnapshot(nextSearchParams.toString());
  }, [nextSearchParams]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncFromWindow = () => {
      setSnapshot(window.location.search.replace(/^\?/, ''));
    };

    window.addEventListener('popstate', syncFromWindow);
    window.addEventListener(SYNC_EVENT_NAME, syncFromWindow);
    return () => {
      window.removeEventListener('popstate', syncFromWindow);
      window.removeEventListener(SYNC_EVENT_NAME, syncFromWindow);
    };
  }, []);

  const read = useCallback(() => {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search);
    }
    return new URLSearchParams(snapshot);
  }, [snapshot]);

  const write = useCallback((params: URLSearchParams) => {
    if (typeof window === 'undefined') {
      return;
    }
    const query = params.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState(window.history.state, '', nextUrl);
    setSnapshot(query);
    window.dispatchEvent(new Event(SYNC_EVENT_NAME));
  }, []);

  const get = useCallback(
    (key: string) => {
      return read().get(key);
    },
    [read]
  );

  const getAll = useCallback(
    (key: string) => {
      return read().getAll(key);
    },
    [read]
  );

  const set = useCallback(
    (key: string, value: string) => {
      const params = read();
      params.set(key, value);
      write(params);
    },
    [read, write]
  );

  const deleteParam = useCallback(
    (key: string) => {
      const params = read();
      params.delete(key);
      write(params);
    },
    [read, write]
  );

  const replaceAll = useCallback(
    (updater: ParamsUpdater) => {
      const params = read();
      updater(params);
      write(params);
    },
    [read, write]
  );

  const toString = useCallback(() => {
    return snapshot;
  }, [snapshot]);

  return useMemo(
    () => ({
      get,
      getAll,
      set,
      delete: deleteParam,
      replaceAll,
      toString
    }),
    [get, getAll, set, deleteParam, replaceAll, toString]
  );
}
